// Procházení výprodeje (stránka akční nabídky `/slevoakce/`, SK `/zlavoakcia/`)
// z vlastního indexu. Plátno a rozhodnutí: docs/design/2026-10-07-vyprodej-prochazeni/.
//
// Proč: akční nabídka je kategorie Shoptetu plněná příznakem akce (~600
// produktů po 12 na 50 stranách). Přiřazený do ní je jediný produkt, takže
// nativní filtr nabízí jednu značku a štítky, které má všechno. Nad mřížkou
// proto stojí dlaždice kořenových kategorií s počty, pod vybranou kategorií čipy
// podkategorií, vlevo vlastní filtr (sleva od, cena, značky, poslední kusy)
// a řazení Nejprodávanější / Největší sleva / Nejlevnější / Nejdražší. Všechno
// z `GET /browse?scope=sale` (search/api/routes-browse.mjs), na místě, stav
// v adrese (`?kategorie=zahrada&sleva=30`). Žlutá dlaždice výprodeje skladu
// (CZ) filtruje taky na místě; pás výprodeje a blok Top produkty tu nejsou.
//
// Kostra stránky zůstává nativní (categoryPage.js: sloupce, hlavička, perex,
// toolbar, SEO blok, poradna), vyměňuje se obsah: filtr uvnitř `#filters`
// (nativní sekce schová CSS), karty v `#products`, stránkování. Toolbar,
// chipy a mobilní lišta se ptají registru listingSource.js.
//
// Fallback jako stránka hledání: na odpověď se čeká nejvýš 1 200 ms
// (searchApi TIMEOUT.browse). Kostra stránky (`html.bs-boot`) po bootu odchází
// jako všude jinde; do odpovědi drží `html.bs-sb-wait` kostru jen toho, co výřez
// vymění (počet, dlaždice, filtr, rozsah, karty), ve finální geometrii
// (_sale-browse.scss). Dlaždice a filtr mají zástupce na svém místě, montáž je
// vymění (`replaceWith`); toolbar a mobilní lišta dostanou finální volby řazení
// hned z čekacího zdroje výpisu (nativní akční nabídka má jiných pět tabů, které
// se na užším desktopu lámou jinak). Cokoli jiného než platná odpověď = nativní
// výpis. Přihlášený velkoobchod jede nativem (akce ceníku 1 pro něj neplatí,
// `getRetailConfig`). Přepínač `saleBrowse` (features.js).

import SHOP from '../../config/shop.js';
import { featureOn } from './features.js';
import { page } from './pages.js';
import { getRetailConfig } from './searchConfig.js';
import { browse } from './searchApi.js';
import { cardNodes } from './productCardRender.js';
import { enhanceCards, observeCards } from './productCard.js';
import { icon, iconHtml } from './icon.js';
import { CAT_ICONS } from './menuModel.js';
import { money } from './money.js';
import { setListingSource } from './listingSource.js';
import { flushListingScroll, listingStats, productsWord, refreshChips } from './categoryPage.js';
import { refreshListingTools } from './listingTools.js';
import { refreshMobileFilter } from './mobileFilter.js';
import { activeCampaign, promoSelected, trackPromo } from './campaign.js';
import { ga4Event } from './ga4.js';
import * as M from './saleBrowseModel.js';
import L from '../locale/cs/saleBrowse.js';
import LC from '../locale/cs/categoryPage.js';

const ON = 'bs-sale-browse';
const LIST_ID = 'sale_browse';
const RESTORE_KEY = 'bs-sale-browse-restore';
const RESTORE_MAX = M.PAGE_SIZE * 4;
const MOBILE_MQ = '(max-width: 768px)';
// mobil: pás ukazuje prvních 8 podkategorií, zbytek schová „Další (N)" (artboard SB-mobil-zahrada)
const MOBILE_CHIPS = 8;
const WAIT = 'bs-sb-wait';
// Zástupci dlaždic do odpovědi: 2 řady po 4 na desktopu (kořenů s akcí bývá 5–7
// a k nim výprodej skladu, 8. 10. 2026: prvních 7 kořenů manifestu + 1), na
// mobilu je pás jedna řada vždy. Nesou texty, aby se zalomily jako hotové dlaždice
// (na 1100 px dva řádky jména i počtu, řada 86 px): texty z poslední montáže na
// téže adrese (návrat z detailu), jinak jména kořenů z menu a dvouciferný počet.
// Tříciferný se na 1137–1180 px láme, i když hotové dlaždice s menšími počty ne.
const WAIT_TILES = 8;
const WAIT_CHIPS = 6;
const WAIT_COUNT = 50; // počet do zástupce, text se nekreslí
const TILES_KEY = 'bs-sale-browse-tiles';

let started = false;

function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

function fmt(value) {
  return money(value, true) || '';
}

// ---------------------------------------------------------------- kde běží

/** Stránka akční nabídky (i `/strana-N/`); offline fixture nese ostrou kanonickou URL. */
function onSalePage() {
  const sale = page('sale');
  if (!sale || !document.body.classList.contains('type-category')) return false;
  const paths = [location.pathname];
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    try {
      paths.push(new URL(canonical.getAttribute('href'), location.href).pathname);
    } catch (e) {
      /* rozhoduje cesta stránky */
    }
  }
  return paths.some(function (p) {
    return p.indexOf(sale) === 0;
  });
}

function shouldRun() {
  return (
    featureOn('saleBrowse') &&
    onSalePage() &&
    !!getRetailConfig() &&
    !!document.querySelector('#products.products-page') &&
    !document.documentElement.classList.contains(ON)
  );
}

// ---------------------------------------------------------------- adresa

function writeUrl(state) {
  try {
    const sale = page('sale');
    // strana N nativního stránkování → výřez začíná vždy od začátku
    const path = location.pathname.indexOf(sale) === 0 ? sale : location.pathname;
    history.replaceState(history.state, '', path + M.writeQuery(location.search, state, L) + location.hash);
  } catch (e) {
    /* bez History API zůstává adresa beze změny */
  }
}

function restoreKey() {
  return location.pathname + location.search;
}

function takeRestore() {
  try {
    const raw = sessionStorage.getItem(RESTORE_KEY);
    sessionStorage.removeItem(RESTORE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return saved && saved.key === restoreKey() && saved.offset > M.PAGE_SIZE ? saved : null;
  } catch (e) {
    return null;
  }
}

function wireRestore(ctx) {
  // `pagehide` chytí i odchod na iOS; návrat z detailu dostane tolik karet, kolik jich bylo
  window.addEventListener('pagehide', function () {
    try {
      sessionStorage.setItem(RESTORE_KEY, JSON.stringify({ key: restoreKey(), offset: ctx.shown, y: window.scrollY }));
    } catch (e) {
      /* private mode */
    }
  });
}

// ---------------------------------------------------------------- měření

function saleName() {
  const campaign = activeCampaign();
  if (campaign) return campaign.name;
  const h1 = document.querySelector('h1');
  return h1 ? (h1.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

function listName(ctx) {
  return L.listName(saleName(), M.sliceName(ctx.state, ctx.data, L));
}

function trackList(ctx, cards, offset) {
  if (!cards.length) return;
  const name = listName(ctx);
  ga4Event('view_item_list', {
    item_list_id: LIST_ID,
    item_list_name: name,
    items: cards.map(function (c, i) {
      return { item_id: c.sku, item_name: c.name, price: c.price, index: offset + i + 1, item_list_id: LIST_ID, item_list_name: name };
    }),
  });
}

function trackFilter(name, value) {
  ga4Event('sale_browse_filter', { filter_name: name, filter_value: value === undefined || value === null ? '' : String(value) });
}

// Proklik karty = `click` + `auxclick` prostředním tlačítkem (vzor searchAnalytics),
// tlačítko košíku proklik není.
function wireClicks(ctx, block) {
  if (block.dataset.bsSaleClicks) return;
  block.dataset.bsSaleClicks = '1';
  const onLink = function (e) {
    if (e.type === 'auxclick' && e.button !== 1) return;
    const target = e.target;
    if (!target || !target.closest || target.closest('button, .add-to-cart-button')) return;
    const link = target.closest('a[href]');
    const card = link && link.closest('[data-bs-code]');
    if (!card || !block.contains(card)) return;
    const cards = block.querySelectorAll('[data-bs-code]');
    const name = listName(ctx);
    ga4Event('select_item', {
      item_list_id: LIST_ID,
      item_list_name: name,
      items: [
        {
          item_id: card.getAttribute('data-bs-code'),
          index: Array.prototype.indexOf.call(cards, card) + 1,
          item_list_id: LIST_ID,
          item_list_name: name,
        },
      ],
    });
  };
  block.addEventListener('click', onLink);
  block.addEventListener('auxclick', onLink);
}

// ---------------------------------------------------------------- dlaždice

function tileButton(opts) {
  const btn = el('button', 'bs-subcat bs-sb-tile' + (opts.cls ? ' ' + opts.cls : ''));
  btn.type = 'button';
  btn.setAttribute('aria-pressed', String(!!opts.active));
  if (opts.active) btn.classList.add('is-active');
  if (opts.disabled) btn.disabled = true;
  const ic = el('span', 'bs-subcat__ic');
  ic.appendChild(icon(opts.icon));
  btn.appendChild(ic);
  const txt = el('span', 'bs-subcat__txt');
  txt.appendChild(el('span', 'bs-subcat__name', opts.name));
  txt.appendChild(el('span', 'bs-subcat__count', opts.sub));
  btn.appendChild(txt);
  if (opts.active) {
    // vybraná dlaždice: křížek místo šipky (desktop v řádku, mobil v rohu)
    const x = el('span', 'bs-sb-tile__x');
    x.appendChild(icon('close'));
    btn.appendChild(x);
  } else {
    btn.appendChild(icon('chevron-down', 'bs-subcat__chev'));
  }
  btn.addEventListener('click', opts.onClick);
  const li = el('li');
  li.appendChild(btn);
  return li;
}

function renderTiles(ctx) {
  const old = document.querySelector('.bs-sb-tiles');
  const tiles = M.tilesModel(ctx.data.facets.roots, SHOP.catalog.roots, ctx.state);
  const list = el('ul', 'bs-subcats bs-sb-tiles');
  list.setAttribute('aria-label', L.tilesLabel);
  // vlna 2 Black Friday: Vánoce ve výprodeji nesou místo počtu claim („až −30 %")
  const live = activeCampaign();
  const xmasPath = live && live.xmas ? SHOP.catalog.roots.xmas : null;

  tiles.forEach(function (t) {
    list.appendChild(
      tileButton({
        cls: xmasPath && t.path === xmasPath ? 'bs-sb-tile--xmas' : '',
        name: t.name,
        sub: xmasPath && t.path === xmasPath && t.count > 0 ? L.clearanceClaim(live.xmas.claimPct) : L.count(t.count),
        icon: CAT_ICONS[t.path] || 'grid',
        active: t.active,
        disabled: !t.active && t.count === 0,
        onClick: function () {
          const next = Object.assign({}, ctx.state, { cat: t.active ? null : t.slug, sub: null });
          trackFilter('category', next.cat);
          update(ctx, next);
        },
      }),
    );
  });

  // Výprodej skladu (CZ): filtr na místě, claim „až −30 %" z kampaně
  const clearanceCount = Number(ctx.data.facets.clearance) || 0;
  if (page('clearance') && (clearanceCount > 0 || ctx.state.clearance)) {
    const campaign = activeCampaign();
    const claim = campaign && campaign.claim;
    const li = tileButton({
      cls: 'bs-sb-tile--clearance',
      name: L.clearanceTitle,
      sub: claim ? L.clearanceClaim(claim) : L.count(clearanceCount),
      icon: CAT_ICONS[page('clearance')] || 'tag',
      active: ctx.state.clearance,
      onClick: function () {
        const on = !ctx.state.clearance;
        if (on && campaign) promoSelected(campaign, 'listing_tile', 'clearance');
        trackFilter('clearance', on ? 1 : 0);
        update(ctx, Object.assign({}, ctx.state, { clearance: on }));
      },
    });
    list.appendChild(li);
    if (campaign) trackPromo(li.firstChild, campaign, 'listing_tile', { target: 'clearance' });
  }

  if (old) old.replaceWith(list);
  else placeTiles(list);
  rememberTiles(list);
  renderChips(ctx, list);
}

/** Texty hotových dlaždic pro zástupce při příštím načtení téže adresy. */
function rememberTiles(list) {
  try {
    const tiles = Array.prototype.map.call(list.querySelectorAll('.bs-sb-tile'), function (t) {
      return { name: t.querySelector('.bs-subcat__name').textContent, sub: t.querySelector('.bs-subcat__count').textContent };
    });
    sessionStorage.setItem(TILES_KEY, JSON.stringify({ key: restoreKey(), tiles: tiles }));
  } catch (e) {
    /* private mode / blokované úložiště */
  }
}

function rememberedTiles() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(TILES_KEY) || 'null');
    const ok = saved && saved.key === restoreKey() && Array.isArray(saved.tiles) && saved.tiles.length;
    const valid = function (t) {
      return t && typeof t.name === 'string' && typeof t.sub === 'string';
    };
    return ok && saved.tiles.every(valid) ? saved.tiles : null;
  } catch (e) {
    return null;
  }
}

/** Pás dlaždic stojí za hlavičkou kategorie (perex), jinak nad toolbarem. */
function placeTiles(list) {
  const h1 = document.querySelector('h1');
  const top = h1 && h1.closest('.category-top');
  const header = document.querySelector('#category-header');
  if (top) top.insertAdjacentElement('afterend', list);
  else if (header) header.insertAdjacentElement('beforebegin', list);
}

// ---------------------------------------------------------------- čipy podkategorií

function chipButton(label, count, active, onClick) {
  const chip = el('button', 'bs-sb-chip' + (active ? ' is-active' : ''));
  chip.type = 'button';
  chip.setAttribute('aria-pressed', String(active));
  chip.appendChild(el('span', 'bs-sb-chip__name', label));
  chip.appendChild(el('span', 'bs-sb-chip__count', String(count)));
  chip.addEventListener('click', onClick);
  return chip;
}

function renderChips(ctx, tiles) {
  const old = document.querySelector('.bs-sb-chips');
  if (old) old.remove();
  const subs = ctx.state.cat ? M.chipsModel(ctx.data.facets.subs, ctx.state) : [];
  if (!subs.length) return;
  const root = M.tilesModel(ctx.data.facets.roots, SHOP.catalog.roots, ctx.state).filter(function (t) {
    return t.active;
  })[0];
  if (!root) return;

  const box = el('div', 'bs-sb-chips');
  box.setAttribute('role', 'group');
  box.setAttribute('aria-label', L.chipsLabel);
  box.appendChild(
    chipButton(M.allInLabel(root.key, L), root.count, !ctx.state.sub, function () {
      if (!ctx.state.sub) return;
      trackFilter('subcategory', '');
      update(ctx, Object.assign({}, ctx.state, { sub: null }));
    }),
  );
  subs.forEach(function (s) {
    box.appendChild(
      chipButton(s.name, s.count, s.active, function () {
        const next = Object.assign({}, ctx.state, { sub: s.active ? null : s.slug });
        trackFilter('subcategory', next.sub);
        update(ctx, next);
      }),
    );
  });
  const more = el('button', 'bs-sb-chip bs-sb-chip--more');
  more.type = 'button';
  more.setAttribute('aria-expanded', 'false');
  more.appendChild(el('span', 'bs-sb-chip__name'));
  more.appendChild(icon('chevron-down'));
  more.addEventListener('click', function () {
    ctx.chipsOpen = !ctx.chipsOpen;
    fitChips(ctx, box);
  });
  box.appendChild(more);
  tiles.insertAdjacentElement('afterend', box);
  // sub vybraná mimo první řádek → rozbaleno, ať je vidět, co platí
  ctx.chipsOpen = !!ctx.chipsOpen;
  requestAnimationFrame(function () {
    fitChips(ctx, box);
  });
}

function setMore(more, open, hiddenCount) {
  const label = more.querySelector('.bs-sb-chip__name');
  label.textContent = open ? L.chipsLess : L.chipsMore(hiddenCount);
  more.setAttribute('aria-expanded', String(open));
  more.classList.toggle('is-open', open);
}

// Desktop: jeden řádek čipů, co se nevejde, schová „Další (N)". Mobil: pás
// se posouvá prstem, za MOBILE_CHIPS podkategoriemi „Další (N)".
function fitChips(ctx, box) {
  if (!box || !box.isConnected) return;
  const chips = Array.prototype.slice.call(box.querySelectorAll('.bs-sb-chip:not(.bs-sb-chip--more)'));
  const more = box.querySelector('.bs-sb-chip--more');
  chips.forEach(function (c) {
    c.hidden = false;
  });
  more.hidden = true;
  if (window.matchMedia && window.matchMedia(MOBILE_MQ).matches) {
    // chips[0] = „Vše v …", podkategorie od indexu 1
    const extra = chips.slice(MOBILE_CHIPS + 1);
    if (!extra.length) return;
    if (!ctx.chipsDecided && extra.some((c) => c.classList.contains('is-active'))) ctx.chipsOpen = true;
    ctx.chipsDecided = true;
    more.hidden = false;
    if (!ctx.chipsOpen) {
      extra.forEach(function (c) {
        c.hidden = true;
      });
    }
    setMore(more, ctx.chipsOpen, extra.length);
    return;
  }
  const top = chips[0].offsetTop;
  const overflow = function () {
    return chips.filter(function (c) {
      return !c.hidden && c.offsetTop > top;
    });
  };
  if (!overflow().length) return;
  const activeHidden = chips.some(function (c) {
    return c.classList.contains('is-active') && c.offsetTop > top;
  });
  if (activeHidden && !ctx.chipsDecided) ctx.chipsOpen = true;
  ctx.chipsDecided = true;
  more.hidden = false;
  if (ctx.chipsOpen) {
    setMore(more, true, 0);
    return;
  }
  overflow().forEach(function (c) {
    c.hidden = true;
  });
  const hiddenCount = function () {
    return chips.filter(function (c) {
      return c.hidden;
    }).length;
  };
  setMore(more, false, hiddenCount());
  // „Další (N)" musí zůstat v prvním řádku — ubírá se od konce, dokud se nevejde
  for (let i = chips.length - 1; i > 0 && more.offsetTop > top; i--) {
    if (chips[i].hidden) continue;
    chips[i].hidden = true;
    setMore(more, false, hiddenCount());
  }
}

// ---------------------------------------------------------------- filtr

// ⛔ Bez třídy `filter-section`: default JS deleguje klik na `.filter-section input`
// do nativního AJAX filtru s `data-url` vstupu, u našeho vstupu `null` → fetch
// „null" vymění obsah a adresa skončí na /slevoakce/null/ (ostrá 8. 10. 2026).
function section(cls, title) {
  const sec = el('div', 'bs-filter__sec bs-sbf__sec ' + cls);
  const h4 = el('h4');
  h4.appendChild(el('span', null, title));
  sec.appendChild(h4);
  return sec;
}

function optionRow(type, name, id, value, label, count, checked) {
  const row = el('div');
  const input = el('input');
  input.type = type;
  input.name = name;
  input.id = id;
  input.value = value;
  input.checked = !!checked;
  input.autocomplete = 'off';
  const lab = el('label', 'filter-label');
  lab.htmlFor = id;
  lab.appendChild(el('span', null, label));
  lab.appendChild(el('span', 'filter-count', String(count)));
  row.appendChild(input);
  row.appendChild(lab);
  return row;
}

function idPart(value) {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');
}

function buildDiscount(ctx) {
  const rows = M.discountRows(ctx.data.facets.discount, ctx.state);
  if (!rows.length) return null;
  const sec = section('bs-sbf__sec--disc', L.discount);
  const fs = el('fieldset');
  rows.forEach(function (r) {
    fs.appendChild(optionRow('radio', 'bs-sb-disc', 'bs-sb-disc-' + r.min, String(r.min), L.discFrom(r.min), r.count, r.checked));
  });
  // radio nejde odškrtnout → druhý klik na vybrané pásmo filtr zruší
  fs.addEventListener('click', function (e) {
    const input = e.target && e.target.closest ? e.target.closest('input[type="radio"]') : null;
    if (!input) return;
    const min = parseInt(input.value, 10);
    const next = Object.assign({}, ctx.state, { disc: ctx.state.disc === min ? null : min });
    trackFilter('discount', next.disc);
    update(ctx, next);
  });
  sec.appendChild(fs);
  return sec;
}

function buildPrice(ctx) {
  const range = M.priceRange(ctx.data.facets.price, ctx.state);
  if (!range) return null;
  const sec = section('bs-sbf__price', L.price);
  const slider = el('div', 'bs-sbf__slider');
  const track = el('div', 'bs-sbf__track');
  const fill = el('div', 'bs-sbf__fill');
  track.appendChild(fill);
  slider.appendChild(track);
  const lo = el('input', 'bs-sbf__range bs-sbf__range--min');
  const hi = el('input', 'bs-sbf__range bs-sbf__range--max');
  [lo, hi].forEach(function (input) {
    input.type = 'range';
    input.min = String(Math.floor(range.min));
    input.max = String(Math.ceil(range.max));
    input.step = '1';
  });
  lo.id = 'bs-sb-price-min';
  hi.id = 'bs-sb-price-max';
  lo.value = String(Math.floor(range.from));
  hi.value = String(Math.ceil(range.to));
  lo.setAttribute('aria-label', L.priceMin);
  hi.setAttribute('aria-label', L.priceMax);
  slider.appendChild(lo);
  slider.appendChild(hi);
  sec.appendChild(slider);

  const head = el('div', 'slider-header bs-sbf__values');
  const from = el('span', 'from');
  const to = el('span', 'to');
  head.appendChild(from);
  head.appendChild(el('span', 'bs-filter__dash', '–'));
  head.appendChild(to);
  sec.appendChild(head);

  const paint = function () {
    const a = Math.min(Number(lo.value), Number(hi.value));
    const b = Math.max(Number(lo.value), Number(hi.value));
    const span = range.max - range.min || 1;
    fill.style.left = ((a - range.min) / span) * 100 + '%';
    fill.style.right = (1 - (b - range.min) / span) * 100 + '%';
    from.textContent = fmt(a);
    to.textContent = fmt(b);
    lo.setAttribute('aria-valuetext', fmt(Number(lo.value)));
    hi.setAttribute('aria-valuetext', fmt(Number(hi.value)));
  };
  paint();
  [lo, hi].forEach(function (input) {
    input.addEventListener('input', paint);
    input.addEventListener('change', function () {
      const next = M.withPrice(ctx.state, range, Number(lo.value), Number(hi.value));
      if (next.pmin === ctx.state.pmin && next.pmax === ctx.state.pmax) return;
      trackFilter('price', (next.pmin === null ? '' : next.pmin) + '-' + (next.pmax === null ? '' : next.pmax));
      update(ctx, next);
    });
  });
  return sec;
}

function buildBrands(ctx) {
  const rows = M.brandRows(ctx.data.facets.brands, ctx.state);
  if (!rows.length) return null;
  const sec = section('bs-sbf__sec--brand', L.brand);
  const fs = el('fieldset');
  rows.forEach(function (r) {
    fs.appendChild(optionRow('checkbox', 'bs-sb-brand', 'bs-sb-brand-' + idPart(r.name), r.name, r.name, r.count, r.checked));
  });
  fs.addEventListener('change', function (e) {
    const input = e.target;
    if (!input || input.name !== 'bs-sb-brand') return;
    const brands = ctx.state.brands.filter(function (b) {
      return b !== input.value;
    });
    if (input.checked) brands.push(input.value);
    trackFilter('brand', input.checked ? input.value : '');
    update(ctx, Object.assign({}, ctx.state, { brands: brands }));
  });
  sec.appendChild(fs);

  // nad BRAND_LIMIT značek „Zobrazit více (N)" jako nativní sekce (categoryPage collapseSection)
  const hidden = Array.prototype.slice.call(fs.children).slice(M.BRAND_LIMIT);
  if (hidden.length) {
    if (hidden.some((row) => row.querySelector('input').checked)) ctx.brandsOpen = true;
    const btn = el('button', 'bs-filter__more');
    btn.type = 'button';
    const label = el('span');
    btn.appendChild(label);
    btn.appendChild(icon('chevron-down'));
    const apply = function () {
      const open = !!ctx.brandsOpen;
      hidden.forEach(function (row) {
        row.classList.toggle('bs-filter__row--hidden', !open);
      });
      btn.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', String(open));
      label.textContent = open ? LC.secLess : LC.secMore(hidden.length);
    };
    btn.addEventListener('click', function () {
      ctx.brandsOpen = !ctx.brandsOpen;
      apply();
    });
    apply();
    sec.appendChild(btn);
  }
  return sec;
}

function buildAvailability(ctx) {
  const count = Number(ctx.data.facets.last) || 0;
  if (!count && !ctx.state.last) return null;
  const sec = section('bs-sbf__sec--last', L.availability);
  const fs = el('fieldset');
  fs.appendChild(optionRow('checkbox', 'bs-sb-last', 'bs-sb-last', '1', L.lastPieces, count, ctx.state.last));
  fs.addEventListener('change', function (e) {
    const on = !!(e.target && e.target.checked);
    trackFilter('last_pieces', on ? 1 : 0);
    update(ctx, Object.assign({}, ctx.state, { last: on }));
  });
  sec.appendChild(fs);
  return sec;
}

function renderFilter(ctx) {
  const filters = document.querySelector('#filters');
  if (!filters) return;
  const focused = document.activeElement && document.activeElement.closest && document.activeElement.closest('.bs-sbf')
    ? document.activeElement.id
    : null;
  const box = el('div', 'bs-sbf');
  [buildDiscount(ctx), buildPrice(ctx), buildBrands(ctx), buildAvailability(ctx)].forEach(function (sec) {
    if (sec) box.appendChild(sec);
  });
  const old = filters.querySelector('.bs-sbf');
  if (old) old.replaceWith(box);
  else placeFilter(filters, box);
  if (focused) {
    const again = document.getElementById(focused);
    if (again) again.focus({ preventScroll: true });
  }
}

/** Sekce filtru stojí pod hlavičkou panelu a chipy aktivních filtrů. */
function placeFilter(filters, box) {
  const anchor = filters.querySelector('.bs-filter__chips') || filters.querySelector('.bs-filter__head');
  if (anchor) anchor.insertAdjacentElement('afterend', box);
  else filters.insertAdjacentElement('afterbegin', box);
}

// ---------------------------------------------------------------- mřížka

function grid() {
  return document.querySelector('#products.products-page');
}

function fillGrid(ctx, cards, append) {
  const block = grid();
  if (!block) return;
  if (!append) block.textContent = '';
  block.appendChild(cardNodes(M.presentCards(cards, SHOP.catalog.clearanceFlag, L)));
  enhanceCards(block);
  observeCards(block);
  wireClicks(ctx, block);
  renderEmpty(ctx);
}

function renderEmpty(ctx) {
  const old = document.querySelector('.bs-sb-empty');
  if (old) old.remove();
  if (ctx.data.total > 0) return;
  const panel = el('div', 'bs-empty bs-empty--filter bs-sb-empty');
  const mark = el('span', 'bs-empty__icon');
  mark.appendChild(icon('search'));
  panel.appendChild(mark);
  panel.appendChild(el('div', 'bs-empty__title', L.emptyTitle));
  panel.appendChild(el('p', 'bs-empty__text', L.emptyText));
  if (M.hasSelection(ctx.state)) {
    const btn = el('button', 'bs-empty__cta', L.emptyCta);
    btn.type = 'button';
    btn.addEventListener('click', function () {
      trackFilter('clear', '');
      update(ctx, M.clearAll(ctx.state));
    });
    panel.appendChild(btn);
  }
  const block = grid();
  if (block) block.insertAdjacentElement('afterend', panel);
}

function renderMore(ctx) {
  let wrap = document.querySelector('.bs-sb-more');
  const block = grid();
  if (!wrap) {
    wrap = el('div', 'bs-sb-more');
    const btn = el('button', 'bs-sb-more__btn');
    btn.type = 'button';
    btn.appendChild(el('span'));
    btn.insertAdjacentHTML('beforeend', iconHtml('arrow-right'));
    btn.addEventListener('click', function () {
      loadMore(ctx, btn);
    });
    wrap.appendChild(btn);
    const progress = el('div', 'bs-sb-more__progress');
    progress.appendChild(el('span'));
    wrap.appendChild(progress);
    wrap.appendChild(el('span', 'bs-sb-more__shown'));
    if (block) block.insertAdjacentElement('afterend', wrap);
  }
  const total = ctx.data.total;
  const rest = Math.max(0, total - ctx.shown);
  const btn = wrap.querySelector('.bs-sb-more__btn');
  btn.hidden = rest === 0;
  btn.querySelector('span').textContent = L.more(Math.min(M.PAGE_SIZE, rest));
  wrap.querySelector('.bs-sb-more__progress > span').style.width = total ? Math.round((ctx.shown / total) * 100) + '%' : '0';
  wrap.querySelector('.bs-sb-more__shown').textContent = total ? L.shown(ctx.shown, total) : '';
  wrap.hidden = total === 0;
}

async function loadMore(ctx, btn) {
  if (btn.disabled) return;
  const seq = ctx.seq;
  btn.disabled = true;
  btn.classList.add('is-loading');
  const data = await browse(M.apiParams(ctx.state, ctx.shown, M.PAGE_SIZE));
  btn.disabled = false;
  btn.classList.remove('is-loading');
  if (seq !== ctx.seq || !M.validResponse(data)) return; // mezitím jiný výběr / chyba: tlačítko zůstává
  const offset = ctx.shown;
  fillGrid(ctx, data.products, true);
  ctx.shown += data.products.length;
  ctx.data = Object.assign({}, ctx.data, { total: data.total });
  renderMore(ctx);
  refreshListingTools();
  trackList(ctx, data.products, offset);
}

// ---------------------------------------------------------------- hlavička

function renderHeading(ctx) {
  const head = document.querySelector('.bs-cat-head');
  if (!head) return;
  let count = head.querySelector('.bs-cat-count');
  if (!count) {
    count = el('span', 'bs-cat-count');
    head.appendChild(count);
  }
  const n = typeof ctx.data.scope_total === 'number' ? ctx.data.scope_total : ctx.data.total;
  count.textContent = n + ' ' + productsWord(n);
}

// ---------------------------------------------------------------- stav

function source(ctx) {
  return {
    stats: function () {
      return { page: 1, pages: 1, total: ctx.data.total, rendered: ctx.shown };
    },
    sortOptions: function () {
      return M.ORDERS.map(function (key) {
        return {
          label: L.sortLabels[key],
          href: null,
          active: ctx.state.order === key,
          activate: function () {
            if (ctx.state.order === key) return;
            trackFilter('sort', key);
            update(ctx, Object.assign({}, ctx.state, { order: key }));
          },
        };
      });
    },
    activeFilters: function () {
      return M.panelFilters(ctx.state, fmt, L).map(function (f) {
        return {
          label: f.label,
          remove: function () {
            trackFilter(f.key, '');
            update(ctx, M.withoutFilter(ctx.state, f.key, f.value));
          },
        };
      });
    },
    clearFilters: function () {
      trackFilter('clear', '');
      update(ctx, M.clearPanel(ctx.state));
    },
  };
}

function render(ctx) {
  renderHeading(ctx);
  renderTiles(ctx);
  renderFilter(ctx);
  renderMore(ctx);
  refreshListingTools();
  refreshChips();
  refreshMobileFilter();
}

/** Nový výběr: adresa hned, výpis po odpovědi (stará odpověď se zahodí). */
async function update(ctx, next) {
  // jiný kořen = jiné čipy, rozbalení „Další" se nedědí
  if (next.cat !== ctx.state.cat) ctx.chipsOpen = false;
  ctx.state = next;
  ctx.chipsDecided = false;
  writeUrl(next);
  const seq = (ctx.seq += 1);
  const block = grid();
  if (block) block.classList.add('is-busy');
  // zaškrtnutí ve filtru, dlaždice a chipy ukazují nový výběr hned
  refreshChips();
  refreshMobileFilter();

  const data = await browse(M.apiParams(next, 0, M.PAGE_SIZE));
  if (seq !== ctx.seq) return;
  if (block) block.classList.remove('is-busy');
  if (!M.validResponse(data)) {
    // výpadek uprostřed procházení: výpis zůstává, nový pokus dá další volba
    try {
      console.warn('[bs] saleBrowse: /browse bez platne odpovedi, vypis zustava');
    } catch (e) {
      /* konzole není */
    }
    return;
  }
  ctx.data = data;
  ctx.shown = data.products.length;
  fillGrid(ctx, data.products, false);
  render(ctx);
  trackList(ctx, data.products, 0);
  // změna ve filtru (change v `#filters`) scrolluje na začátek produktů jako
  // nativní filtr; s otevřeným mobilním filtrem až po jeho zavření
  if (!document.body.classList.contains('bs-fsheet-open')) flushListingScroll();
}

function mount(state, data, restore) {
  const ctx = { state: state, data: data, shown: data.products.length, seq: 0, brandsOpen: false, chipsOpen: false };
  document.documentElement.classList.add(ON);
  setListingSource(source(ctx));
  // pás výprodeje skladu stojí z bootu (categoryPage) — tady ho nahrazuje dlaždice
  const band = document.querySelector('.bs-camp-band');
  if (band) band.remove();
  fillGrid(ctx, data.products, false);
  render(ctx);
  writeUrl(state);
  wireRestore(ctx);
  trackList(ctx, data.products, 0);
  if (restore && restore.y) {
    requestAnimationFrame(function () {
      window.scrollTo(0, restore.y);
    });
  }
  let resizeTimer = 0;
  window.addEventListener(
    'resize',
    function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        fitChips(ctx, document.querySelector('.bs-sb-chips'));
      }, 150);
    },
    { passive: true },
  );
  return ctx;
}

// ---------------------------------------------------------------- čekání na index

/** Jména kořenů z nativního menu podle cesty (index je nese stejná). */
function menuRootNames() {
  const names = {};
  document.querySelectorAll('#navigation ul.menu-level-1 > li > a[href]').forEach(function (a) {
    try {
      const path = new URL(a.getAttribute('href'), location.href).pathname;
      const name = ((a.querySelector('b') || a).textContent || '').replace(/\s+/g, ' ').trim();
      if (name) names[path] = name;
    } catch (e) {
      /* odkaz bez cesty */
    }
  });
  return names;
}

/** Texty zástupců: z poslední montáže, jinak kořeny manifestu (jména z menu) a výprodej skladu. */
function waitTileTexts() {
  const remembered = rememberedTiles();
  if (remembered) return remembered;
  const names = menuRootNames();
  const campaign = page('clearance') ? activeCampaign() : null;
  const clearance = page('clearance')
    ? [{ name: L.clearanceTitle, sub: campaign && campaign.claim ? L.clearanceClaim(campaign.claim) : L.count(WAIT_COUNT) }]
    : [];
  const roots = Object.keys(SHOP.catalog.roots || {})
    .map(function (k) {
      return names[SHOP.catalog.roots[k]];
    })
    .filter(Boolean)
    .slice(0, WAIT_TILES - clearance.length)
    .map(function (name) {
      return { name: name, sub: L.count(WAIT_COUNT) };
    });
  if (!roots.length) {
    // menu bez kořenů: prázdné řádky drží aspoň výšku písma
    for (let i = clearance.length; i < WAIT_TILES; i += 1) roots.push({ name: '\u00A0', sub: '\u00A0' });
  }
  return roots.concat(clearance);
}

function waitTile(text) {
  const tile = el('span', 'bs-subcat bs-sb-tile');
  tile.appendChild(el('span', 'bs-subcat__ic'));
  const txt = el('span', 'bs-subcat__txt');
  txt.appendChild(el('span', 'bs-subcat__name', text.name));
  txt.appendChild(el('span', 'bs-subcat__count', text.sub));
  tile.appendChild(txt);
  tile.appendChild(icon('chevron-down', 'bs-subcat__chev')); // bere šířku textu jako u hotové dlaždice
  const li = el('li');
  li.appendChild(tile);
  return li;
}

/**
 * Zdroj výpisu do odpovědi: volby řazení a chipy z adresy jako po montáži, počty
 * z nativu (`rendered` drží rozsah neprázdný, ať se toolbar neláme jinak). Volby
 * nic nedělají, výběr přijme až zdroj montáže.
 */
function waitSource(state, stats) {
  const inert = function () {};
  return {
    stats: function () {
      return stats;
    },
    sortOptions: function () {
      return M.ORDERS.map(function (key) {
        return { label: L.sortLabels[key], href: null, active: state.order === key, activate: inert };
      });
    },
    activeFilters: function () {
      return M.panelFilters(state, fmt, L).map(function (f) {
        return { label: f.label, remove: inert };
      });
    },
    clearFilters: inert,
  };
}

/** Kostra výřezu do odpovědi indexu: třída na <html> a zástupci na místech, kam montáž kreslí. */
function startWait(state) {
  document.documentElement.classList.add(WAIT);
  const stats = listingStats(); // nativní, zdroj ještě není
  setListingSource(waitSource(state, stats));
  refreshListingTools();
  refreshChips();
  refreshMobileFilter();
  const tiles = el('ul', 'bs-subcats bs-sb-tiles bs-sb-tiles--wait');
  tiles.setAttribute('aria-hidden', 'true');
  waitTileTexts().forEach(function (text) {
    tiles.appendChild(waitTile(text));
  });
  placeTiles(tiles);
  if (state.cat && tiles.isConnected) {
    // vybraný kořen z adresy: pod dlaždicemi stojí řada čipů podkategorií
    const chips = el('div', 'bs-sb-chips bs-sb-chips--wait');
    chips.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < WAIT_CHIPS; i += 1) chips.appendChild(el('span', 'bs-sb-chip'));
    tiles.insertAdjacentElement('afterend', chips);
  }
  const filters = document.querySelector('#filters');
  if (filters) {
    const box = el('div', 'bs-sbf bs-sbf--wait');
    box.setAttribute('aria-hidden', 'true');
    placeFilter(filters, box);
  }
}

/** Konec čekání: montáž zástupce vyměnila; bez ní zpět k nativní vrstvě. */
function endWait(mounted) {
  document.documentElement.classList.remove(WAIT);
  document.querySelectorAll('.bs-sb-tiles--wait, .bs-sb-chips--wait, .bs-sbf--wait').forEach(function (n) {
    n.remove();
  });
  if (mounted) return;
  // toolbar, chipy a mobilní lišta se ptají registru; mřížka mohla zůstat
  // z indexu (chyba uprostřed montáže), nativní filtr a stránkování se ukážou
  document.documentElement.classList.remove(ON);
  setListingSource(null);
  refreshListingTools();
  refreshChips();
  refreshMobileFilter();
}

/**
 * Boot: do odpovědi indexu kostra výřezu (`bs-sb-wait`), pak montáž, nebo nativní
 * výpis. Čekání skončí vždy, chyba montáže = nativní výpis + `[bs]` hláška.
 */
export function initSaleBrowse() {
  if (started || !shouldRun()) return;
  started = true;
  const state = M.parseState(location.search, L);
  const restore = takeRestore();
  const limit = restore ? Math.min(restore.offset, RESTORE_MAX) : M.PAGE_SIZE;
  startWait(state);
  browse(M.apiParams(state, 0, limit)).then(
    function (data) {
      let mounted = false;
      try {
        if (M.validResponse(data)) {
          mount(state, data, restore);
          mounted = true;
        }
      } catch (err) {
        try {
          console.error('[bs] saleBrowse montaz selhala', err);
        } catch (e) {
          /* konzole není */
        }
      }
      // týž úkol jako montáž → mezi výřezem a koncem kostry se nekreslí
      endWait(mounted);
    },
    function () {
      /* searchApi nehází; nativní výpis zůstává */
      endWait(false);
    },
  );
}
